import {
	useCurrentGameProfileQueryIfGameAvailable
} from "@entities/user/api/useCurrentGameProfileQueryIfGameAvailable.ts";
import { FxIdSdk } from "@fxid/sdk";
import { Alert, Button, Group, Select, Stack, Text, Textarea, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { closeModal, ContextModalProps } from "@mantine/modals";
import { useWebPlayerUiStore } from "@pages/WebPlayerPage/store/webPlayerStore.ts";
import { validateEmailWithMessage } from "@shared/lib/emailValidation";
import { IconAlertCircle, IconBug, IconCheck, IconMail, IconSend } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import OpenApiClient from "../../../../packages/fxid-openapi-client/src/OpenApiClient.ts";

export type ReportIssueModalInnerProps = {
	gameData: string;
	gameSystemName?: string;
};

interface FormValues {
	message: string;
	email: string;
	subject: string;
}

const pauseGame = (fxidInstance: FxIdSdk | null | undefined) => {
	if (fxidInstance == null) {
		return;
	}
	fxidInstance.DispatchDisableKeyboardInputCapture();
};

const resumeGame = (fxidInstance: FxIdSdk | null | undefined) => {
	if (fxidInstance == null) {
		return;
	}
	fxidInstance.DispatchEnableKeyboardInputCapture();
};

const getSubjectDisplayLabel = (value: string, t: any): string => {
	const subjectLabels: Record<string, string> = {
		bug_report: t("bug_report", "Bug Report"),
		payment_issues: t("modalSupport_payments", "Payment Issues"),
		account_issues: t("modalSupport_account-issues", "Account Issues"),
		technical_issues: t("modalSupport_technical", "Technical Issues"),
		gameplay_issues: t("report_issue_modal.subject.gameplay_issues", "Gameplay Issues"),
		other: t("report_issue_modal.subject.other", "Other")
	};
	return subjectLabels[value] || value;
};

export const ReportIssueModal = (props: ContextModalProps<ReportIssueModalInnerProps>) => {
	const { t } = useTranslation();
	const { innerProps } = props;
	const { data } = useCurrentGameProfileQueryIfGameAvailable();
	const fxidInstance = useWebPlayerUiStore((state) => state.fxidInstance);

	// Pause game when modal is open
	useEffect(() => {
		pauseGame(fxidInstance);

		return () => {
			resumeGame(fxidInstance);
		};
	}, [fxidInstance]);

	const form = useForm<FormValues>({
		initialValues: {
			message: "",
			email: data?.Email ?? "",
			subject: "bug_report"
		},
		validateInputOnBlur: true,
		validate: {
			message: (value) => {
				if (!value || !value.trim()) return t("report_issue_modal.message.required", "Message is required");
				if (value.trim().length < 10) return t("report_issue_modal.message.min_length", "Message must be at least 10 characters");
				return null;
			},
			email: (value) => validateEmailWithMessage(value),
			subject: (value) => {
				if (!value) return t("report_issue_modal.subject.required", "Subject is required");
				return null;
			}
		}
	});

	const { mutate: reportIssue, isPending: isLoading } = useMutation({
		mutationFn: async (data: FormValues) => {
			return await OpenApiClient.Support.fxIdWebFeaturesSupportSendSupportMessageSendSupportMessageEndpoint({
				GameSystemName: innerProps.gameSystemName || "unknown",
				Data: {
					Body: data.message,
					Email: data.email,
					Subject: data.subject,
					DeviceUid: innerProps.gameData,
					Name: data.email.split("@")[0] // Extract name from email
				}
			});
		},
		onSuccess: () => {
			closeModal(props.id);
		},
		onError: (error) => {
		}
	});

	const handleSubmit = (values: FormValues) => {
		reportIssue(values);
	};

	return (
		<Stack>
			<Group gap="sm" align="center">
				<IconBug size={24} />
				<Title order={3}>{t("report_issue_modal.title", "Report Issue")}</Title>
			</Group>

			{/* Info Alert */}
			<Alert variant="light" icon={<IconAlertCircle />}>
				<Text size="sm">{t("report_issue_modal.description", "Please describe the issue you encountered. We will review your report and get back to you.")}</Text>
			</Alert>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack gap="md">
					{/* Subject Field */}
					<Select
						label={t("report_issue_modal.subject.label", "Subject")}
						placeholder={t("report_issue_modal.subject.placeholder", "Select issue type")}
						description={t("report_issue_modal.subject.description", "What type of problem are you experiencing?")}
						withAsterisk
						data={[
							{
								value: "bug_report",
								label: t("bug_report", "Bug Report")
							},
							{
								value: "payment_issues",
								label: t("modalSupport_payments", "Payment Issues")
							},
							{
								value: "account_issues",
								label: t("modalSupport_account-issues", "Account Issues")
							},
							{
								value: "technical_issues",
								label: t("modalSupport_technical", "Technical Issues")
							},
							{
								value: "gameplay_issues",
								label: t("report_issue_modal.subject.gameplay_issues", "Gameplay Issues")
							},
							{
								value: "other",
								label: t("report_issue_modal.subject.other", "Other")
							}
						]}
						{...form.getInputProps("subject")}
					/>

					{/* Message Field */}
					<Textarea
						label={t("report_issue_modal.message.label", "Message")}
						placeholder={t("report_issue_modal.message.default", "Please describe what happened, what you expected to happen, and any steps to reproduce the issue...")}
						description={t("report_issue_modal.message.description", "Provide as much detail as possible")}
						minRows={4}
						maxRows={8}
						autosize
						withAsterisk
						{...form.getInputProps("message")}
					/>

					{/* Email Field */}
					<TextInput
						label={t("report_issue_modal.email.label", "Email")}
						placeholder="your.email@example.com"
						description={t("report_issue_modal.email.description", "We'll use this to contact you about your report")}
						withAsterisk
						leftSection={<IconMail size={16} />}
						{...form.getInputProps("email")}
					/>

					{/* Action Buttons */}
					<Group justify="flex-end" mt="md">
						<Button variant="outline" onClick={() => closeModal(props.id)} disabled={isLoading}>
							{t("common:cancel", "Cancel")}
						</Button>
						<Button
							type="submit"
							leftSection={<IconSend size={16} />}
							loading={isLoading}


						>
							{t("report_issue_modal.send_report", "Send Report")}
						</Button>
					</Group>
				</Stack>
			</form>
			<style>
				{`
					@keyframes modalFadeIn {
						from {
							opacity: 0;
							transform: translateY(10px);
						}
						to {
							opacity: 1;
							transform: translateY(0);
						}
					}
				`}
			</style>
		</Stack>
	);
};
